import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { streamPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import MarkdownLite from "@/components/MarkdownLite";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  LayoutDashboard, Terminal, Library, BookPlus, ShieldAlert, RefreshCw, LogOut, Send, Loader2,
  Users, UserCheck, CreditCard, Eye, Database, GitBranch, Scale, Cpu, Save, BarChart3
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "insights", label: "Visitor Insights", icon: BarChart3 },
  { key: "console", label: "Leego Console", icon: Terminal },
  { key: "model", label: "AI Model", icon: Cpu },
  { key: "frameworks", label: "Legal Frameworks", icon: GitBranch },
  { key: "regulations", label: "Regulations", icon: BookPlus },
  { key: "security", label: "Security Monitor", icon: ShieldAlert },
  { key: "sync", label: "Data Sync", icon: RefreshCw },
];

export default function OwnerConsole() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  const loadStats = () => api.get("/owner/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      <aside className="w-64 shrink-0 border-r border-border bg-[#0B1120] flex flex-col">
        <div className="p-4 border-b border-border"><Brand size="sm" /></div>
        <div className="px-4 py-3 border-b border-border">
          <span className="overline text-primary">Owner / Operator</span>
          <p className="text-xs text-muted-foreground mt-1 truncate">{user?.email}</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`owner-tab-${t.key}`}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm border-l-2 transition-colors ${tab === t.key ? "bg-secondary border-primary text-foreground" : "border-transparent text-muted-foreground hover:bg-secondary/50"}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border space-y-2">
          <button onClick={() => navigate("/app")} data-testid="owner-view-visitor-btn"
                  className="w-full flex items-center gap-2 text-sm text-muted-foreground hover:text-primary px-2 py-1.5 transition-colors">
            <Eye className="w-4 h-4" /> View visitor app
          </button>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="owner-logout-btn"
                  className="w-full flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive px-2 py-1.5 transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-8">
          {tab === "overview" && <Overview stats={stats} refresh={loadStats} />}
          {tab === "insights" && <Insights />}
          {tab === "console" && <Console />}
          {tab === "model" && <ModelSettings />}
          {tab === "frameworks" && <Frameworks refresh={loadStats} />}
          {tab === "regulations" && <Regulations refresh={loadStats} />}
          {tab === "security" && <SecurityMonitor />}
          {tab === "sync" && <Sync />}
        </div>
      </main>
    </div>
  );
}

function Header({ over, title, sub }) {
  return (
    <div className="mb-8">
      <span className="overline text-primary">{over}</span>
      <h1 className="font-serif-display text-3xl font-semibold tracking-tight mt-2">{title}</h1>
      {sub && <p className="text-muted-foreground text-sm mt-2 max-w-2xl">{sub}</p>}
    </div>
  );
}

function Overview({ stats, refresh }) {
  const cards = [
    { label: "Free visitors", value: stats?.free_visitors, icon: Users },
    { label: "Paid visitors", value: stats?.paid_visitors, icon: CreditCard },
    { label: "Subscribers", value: stats?.subscribers, icon: UserCheck },
    { label: "Anonymous visits", value: stats?.anonymous_visits, icon: Eye },
    { label: "Total accounts", value: stats?.total_users, icon: Users },
    { label: "Regulations in DB", value: stats?.regulations, icon: Database },
    { label: "Legal frameworks", value: stats?.frameworks, icon: GitBranch },
  ];
  return (
    <div>
      <div className="flex items-start justify-between">
        <Header over="Command Center" title="Platform Overview"
                sub="Live counters for visitors and subscribers, plus the size of Leego's knowledge base." />
        <Button onClick={refresh} variant="outline" data-testid="refresh-stats-btn" className="rounded-none border-border">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-card p-5" data-testid={`stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>
              <Icon className="w-4 h-4 text-primary mb-3" strokeWidth={1.5} />
              <div className="font-serif-display text-3xl font-semibold">{c.value ?? "—"}</div>
              <div className="overline text-muted-foreground mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PAGE_LABELS = { home: "Home", legal: "Terms & privacy", legal_updates: "Recent law changes", pricing: "Pricing", login: "Sign in", other: "Other" };

function BarList({ title, rows, empty = "Nothing yet." }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-border bg-card p-5">
      <div className="overline text-muted-foreground mb-4">{title}</div>
      {rows.length === 0 && <p className="text-sm text-muted-foreground">{empty}</p>}
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="flex justify-between gap-3 text-sm mb-1">
              <span className="text-foreground/90 break-words">{r.label}</span>
              <span className="font-mono-tech text-xs text-muted-foreground shrink-0">{r.count}</span>
            </div>
            <div className="h-1.5 bg-secondary"><div className="h-1.5 bg-primary" style={{ width: `${(r.count / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Insights() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/owner/insights", { params: { days } }).then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load visitor insights")).finally(() => setLoading(false));
  };
  useEffect(load, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const cards = [
    { label: "New accounts", value: data?.new_accounts, icon: Users },
    { label: "People asking Leego", value: data?.people_asking, icon: UserCheck },
    { label: "Questions asked", value: data?.questions, icon: Terminal },
    { label: "Drafting requests", value: data?.drafting_answers, icon: BookPlus },
    { label: "Files uploaded", value: data?.files_uploaded, icon: Library },
    { label: "Checkouts started", value: data?.checkouts_started, icon: CreditCard },
  ];
  const pages = Object.entries(data?.page_views || {}).map(([k, count]) => ({ label: PAGE_LABELS[k] || k, count }))
    .sort((a, b) => b.count - a.count);
  const citations = (data?.top_citations || []).map((c) => ({ label: `${c.citation}${c.title ? ` — ${c.title}` : ""}`, count: c.count }));

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <Header over="Visitors" title="What Visitors Need"
                sub="Totals for the legal areas people bring to Leego, where Leego came up short, and which pages they read. Your own account is left out. Individual chats and files are never shown here, as the privacy notice promises." />
        <div className="flex gap-2 shrink-0">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32 rounded-none" data-testid="insights-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={load} disabled={loading} variant="outline" data-testid="insights-refresh-btn" className="rounded-none border-border">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border mb-6">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-card p-5" data-testid={`insight-${c.label.toLowerCase().replace(/ /g, "-")}`}>
              <Icon className="w-4 h-4 text-primary mb-3" strokeWidth={1.5} />
              <div className="font-serif-display text-3xl font-semibold">{c.value ?? "—"}</div>
              <div className="overline text-muted-foreground mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>

      <div className="border border-primary/30 bg-primary/5 p-5 mb-6">
        <div className="overline text-primary mb-2">Where Leego came up short</div>
        <p className="text-sm text-muted-foreground">
          <strong className="text-foreground">{data?.answers_without_sources ?? "—"}</strong> of {data?.answers ?? "—"} answers found no matching law in Leego's database, which points to gaps worth adding under Regulations.{" "}
          <strong className="text-foreground">{data?.failed_answers ?? "—"}</strong> answers failed with an error (often the AI model's free-tier limit).
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <BarList title="Matter types" rows={(data?.by_topic || []).map((r) => ({ label: r.label, count: r.count }))} />
        <BarList title="Laws Leego relied on most" rows={citations} />
        <BarList title="Federal or state" rows={(data?.by_scope || []).map((r) => ({ label: r.label, count: r.count }))} />
        <BarList title="States" rows={(data?.by_state || []).map((r) => ({ label: r.label, count: r.count }))} />
        <BarList title="Public page views (all time)" rows={pages} />
      </div>
    </div>
  );
}

function Console() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      await streamPost("/owner/chat", { text }, (delta) => setMessages((m) => {
        const last = m[m.length - 1];
        if (!last || last.role !== "assistant") return m;
        const c = [...m]; c[c.length - 1] = { ...last, content: last.content + delta }; return c;
      }));
    } catch (e) { toast.error(e.message); } finally { setStreaming(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <Header over="Build Copilot" title="Leego AI — Operator Console"
              sub="Command Leego to expand legal coverage, add branches of law, plan redesigns, and manage the knowledge base." />
      <div ref={ref} className="flex-1 overflow-y-auto border border-border bg-[#0B1120] p-4 space-y-4 font-mono-tech text-sm">
        {messages.length === 0 && (
          <div className="text-muted-foreground">
            <p className="text-primary">leego@hardknock:~$ awaiting command…</p>
            <p className="mt-2 text-xs">Try: "Add a new branch for OSHA whistleblower retaliation" · "Draft a plan to add Immigration law coverage" · "Summarize what changed in Part 18 recently"</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className="animate-rise">
            {m.role === "user" ? (
              <div className="text-primary">leego@hardknock:~$ <span className="text-foreground">{m.content}</span></div>
            ) : (
              <div className="flex gap-2.5 pt-1">
                <Scale className="w-4 h-4 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                <div className="flex-1 min-w-0 font-sans">
                  {m.content ? <MarkdownLite text={m.content} /> : <span className="text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…</span>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-end gap-2 border border-border bg-card mt-3 focus-within:border-primary/50 transition-colors">
        <span className="pl-3 pb-3.5 text-primary font-mono-tech text-sm">$</span>
        <Textarea value={input} onChange={(e) => setInput(e.target.value)} data-testid="owner-console-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Command Leego…" rows={1}
                  className="flex-1 resize-none border-0 bg-transparent focus-visible:ring-0 min-h-[48px] text-sm font-mono-tech" />
        <Button onClick={send} disabled={streaming || !input.trim()} data-testid="owner-console-send"
                className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 h-10 w-10 p-0 m-1">
          {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

function ModelSettings() {
  const [data, setData] = useState({ default_model: "", paid_model: "", available: [] });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/owner/settings").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/owner/settings", { default_model: data.default_model, paid_model: data.paid_model });
      toast.success("AI model settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const labelFor = (k) => data.available.find((m) => m.key === k)?.label || k;

  return (
    <div>
      <Header over="Model Control" title="Leego AI Model"
              sub="ChatGPT models power Leego by default. Choose which model free visitors use and which premium model is reserved for paid subscribers and you." />
      <div className="border border-border bg-card p-6 max-w-2xl space-y-6">
        <div>
          <Label className="overline text-muted-foreground">Default model — Free visitors</Label>
          <Select value={data.default_model} onValueChange={(v) => setData((d) => ({ ...d, default_model: v }))}>
            <SelectTrigger data-testid="default-model-select" className="mt-1.5 rounded-none bg-secondary border-border"><SelectValue placeholder="Select model" /></SelectTrigger>
            <SelectContent>{data.available.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline text-muted-foreground">Premium model — Essential / Professional &amp; Owner</Label>
          <Select value={data.paid_model} onValueChange={(v) => setData((d) => ({ ...d, paid_model: v }))}>
            <SelectTrigger data-testid="paid-model-select" className="mt-1.5 rounded-none bg-secondary border-border"><SelectValue placeholder="Select model" /></SelectTrigger>
            <SelectContent>{data.available.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="border border-border/60 bg-background/40 p-4 text-sm space-y-1.5">
          <div className="flex items-center gap-2"><Cpu className="w-4 h-4 text-primary" /> <span className="overline text-muted-foreground">Active routing</span></div>
          <div className="font-mono-tech text-xs text-muted-foreground">Free → <span className="text-foreground">{labelFor(data.default_model)}</span></div>
          <div className="font-mono-tech text-xs text-muted-foreground">Paid / Owner → <span className="text-primary">{labelFor(data.paid_model)}</span></div>
        </div>
        <Button onClick={save} disabled={busy} data-testid="save-model-settings-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save model settings
        </Button>
      </div>
    </div>
  );
}

function Frameworks({ refresh }) {
  const [data, setData] = useState({ builtin: [], custom: [] });
  const [name, setName] = useState("");
  const [group, setGroup] = useState("");
  const [description, setDescription] = useState("");
  const load = () => api.get("/owner/frameworks").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name || !group) { toast.error("Name and group are required"); return; }
    try {
      await api.post("/owner/frameworks", { name, group, description });
      toast.success("Framework added");
      setName(""); setGroup(""); setDescription(""); load(); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <Header over="Knowledge Base" title="Legal Frameworks"
              sub="HardKnockLegal is not limited to employment law. Add new branches of law so Leego can research beyond the built-in OALJ set." />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card p-6">
          <h3 className="font-serif-display text-lg font-semibold mb-4">Add a branch of law</h3>
          <div className="space-y-4">
            <div><Label className="overline text-muted-foreground">Framework name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="framework-name-input"
                     placeholder="e.g., Immigration Law — BALCA" className="mt-1.5 rounded-none bg-secondary border-border" /></div>
            <div><Label className="overline text-muted-foreground">Group</Label>
              <Input value={group} onChange={(e) => setGroup(e.target.value)} data-testid="framework-group-input"
                     placeholder="e.g., Immigration" className="mt-1.5 rounded-none bg-secondary border-border" /></div>
            <div><Label className="overline text-muted-foreground">Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="framework-desc-input"
                        placeholder="Scope of this framework…" className="mt-1.5 rounded-none bg-secondary border-border resize-none" rows={3} /></div>
            <Button onClick={add} data-testid="add-framework-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 w-full">
              <GitBranch className="w-4 h-4 mr-2" /> Add framework
            </Button>
          </div>
        </div>
        <div>
          <div className="overline text-muted-foreground mb-3">Active coverage ({data.builtin.length + data.custom.length})</div>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
            {data.builtin.map((b) => (
              <div key={b.key} className="flex items-center gap-2 border border-border/60 bg-card px-3 py-2.5 text-sm">
                <Library className="w-3.5 h-3.5 text-primary shrink-0" /> <span className="flex-1">{b.name}</span>
                <span className="overline text-muted-foreground">built-in</span>
              </div>
            ))}
            {data.custom.map((b) => (
              <div key={b.key} className="flex items-center gap-2 border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm">
                <GitBranch className="w-3.5 h-3.5 text-primary shrink-0" /> <span className="flex-1">{b.name}</span>
                <span className="overline text-primary">custom</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Regulations({ refresh }) {
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({ citation: "", title: "", branch: "", text: "", source_url: "" });
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const add = async () => {
    if (!form.citation || !form.title || !form.branch || !form.text) { toast.error("Fill all required fields"); return; }
    try {
      await api.post("/owner/regulations", form);
      toast.success(`Added ${form.citation}`);
      setForm({ citation: "", title: "", branch: "", text: "", source_url: "" }); refresh();
    } catch (e) { toast.error("Failed to add"); }
  };

  return (
    <div>
      <Header over="Knowledge Base" title="Add Regulation Record"
              sub="Insert a new regulation section into Leego's grounded database — citation, title, official text, and source URL." />
      <div className="border border-border bg-card p-6 max-w-2xl space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="overline text-muted-foreground">Citation *</Label>
            <Input value={form.citation} onChange={(e) => set("citation", e.target.value)} data-testid="reg-citation-input"
                   placeholder="29 C.F.R. § 18.xx" className="mt-1.5 rounded-none bg-secondary border-border" /></div>
          <div><Label className="overline text-muted-foreground">Branch *</Label>
            <Select value={form.branch} onValueChange={(v) => set("branch", v)}>
              <SelectTrigger data-testid="reg-branch-select" className="mt-1.5 rounded-none bg-secondary border-border"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.key} value={b.key}>{b.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <div><Label className="overline text-muted-foreground">Title *</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="reg-title-input"
                 placeholder="Section title" className="mt-1.5 rounded-none bg-secondary border-border" /></div>
        <div><Label className="overline text-muted-foreground">Official text *</Label>
          <Textarea value={form.text} onChange={(e) => set("text", e.target.value)} data-testid="reg-text-input"
                    placeholder="The regulation text / faithful summary…" className="mt-1.5 rounded-none bg-secondary border-border resize-none" rows={5} /></div>
        <div><Label className="overline text-muted-foreground">Official source URL</Label>
          <Input value={form.source_url} onChange={(e) => set("source_url", e.target.value)} data-testid="reg-source-input"
                 placeholder="https://www.ecfr.gov/…" className="mt-1.5 rounded-none bg-secondary border-border" /></div>
        <Button onClick={add} data-testid="add-regulation-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
          <BookPlus className="w-4 h-4 mr-2" /> Add to database
        </Button>
      </div>
    </div>
  );
}

function SecurityMonitor() {
  const [events, setEvents] = useState([]);
  const [throttles, setThrottles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/owner/security-events").then((r) => {
      setEvents(r.data.events || []);
      setThrottles(r.data.active_throttles || []);
    }).catch(() => toast.error("Failed to load security events")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <Header over="Security" title="Injection & Attack Monitor"
              sub="Leego watches visitor chat input for prompt-injection and common attack-pattern signatures. Matches are logged and emailed to you; repeat offenders get a short automatic cooldown. Leego never applies changes to the site itself." />
      <div className="flex items-center justify-between mb-4">
        <span className="overline text-muted-foreground">{events.length} recent event(s)</span>
        <Button onClick={load} disabled={loading} data-testid="security-refresh-btn"
                className="rounded-none bg-secondary text-foreground hover:bg-secondary/80">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />} Refresh
        </Button>
      </div>

      {throttles.length > 0 && (
        <div className="border border-destructive/40 bg-destructive/5 p-4 mb-6 max-w-2xl">
          <div className="overline text-destructive mb-2">Active cooldowns ({throttles.length})</div>
          {throttles.map((t, i) => (
            <div key={i} className="text-sm text-muted-foreground">{t._id} — {t.reason}</div>
          ))}
        </div>
      )}

      <div className="border border-border bg-card max-w-2xl divide-y divide-border">
        {events.length === 0 && !loading && (
          <div className="p-6 text-sm text-muted-foreground">No suspicious activity detected yet.</div>
        )}
        {events.map((e, i) => (
          <div key={i} data-testid={`security-event-${i}`} className="p-4 text-sm">
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className="w-3.5 h-3.5 text-destructive shrink-0" />
              <span className="font-mono-tech text-xs uppercase text-destructive">{e.category}</span>
              {e.countermeasure && <span className="overline text-primary">{e.countermeasure}</span>}
            </div>
            <p className="text-muted-foreground text-xs mb-1">{e.identity} · {e.ip} · {e.detected_at ? new Date(e.detected_at).toLocaleString() : ""}</p>
            <p className="text-xs text-foreground/80 break-words">{e.snippet}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SyncCard({ endpoint, testId, title, domain }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post(endpoint); setResult(r.data); toast.success("Source check complete"); }
    catch (e) { toast.error("Sync failed"); } finally { setBusy(false); }
  };
  return (
    <div className="border border-border bg-card p-6 max-w-2xl">
      <div className="flex items-center gap-3 mb-5">
        <Database className="w-5 h-5 text-primary" />
        <div><div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-muted-foreground font-mono-tech">{domain}</div></div>
      </div>
      <Button onClick={run} disabled={busy} data-testid={testId} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />} Check official source now
      </Button>
      {result && (
        <div className="mt-5 border border-border/60 bg-background/40 p-4 text-sm space-y-1.5 font-mono-tech">
          <div>Source reachable: <span className={result.official_source_reachable ? "text-[#10B981]" : "text-destructive"}>{String(result.official_source_reachable)}</span></div>
          <div>Status: <span className="text-foreground">{result.status}</span></div>
          {typeof result.added === "number" && (
            <div>Sections ingested: <span className="text-[#10B981]">+{result.added} new</span> · <span className="text-primary">{result.updated} updated</span></div>
          )}
          <div>Checked at: <span className="text-muted-foreground">{new Date(result.checked_at).toLocaleString()}</span></div>
          <p className="text-xs text-muted-foreground mt-2 font-sans">{result.note}</p>
        </div>
      )}
    </div>
  );
}

function Sync() {
  return (
    <div>
      <Header over="Automation" title="Official-Source Data Sync"
              sub="Check each official source of truth and record a retrieval timestamp. Both also run automatically every 24 hours." />
      <div className="space-y-6">
        <SyncCard endpoint="/owner/sync-dol" testId="sync-dol-btn"
                   title="DOL OALJ — Rules of Practice & Procedure" domain="dol.gov/agencies/oalj" />
        <SyncCard endpoint="/owner/sync-tax" testId="sync-tax-btn"
                   title="U.S. Code Title 26 — Individual Income Tax" domain="uscode.house.gov" />
      </div>
    </div>
  );
}
